import type { CSSProperties } from "react"
import type { Column } from "@tanstack/react-table"
import cn from "@/v2-ui/utils/utils.cn"
import DropdownMenu from "@/v2-ui/dropdownMenu"
import Icon from "@/v2-ui/icon"
import { useOverflowPopover } from "../table.overflow"
import { getColumnTitle } from "../table.utils"
import { SUMMARY_LIST_AGGREGATIONS } from "./table.summary.constants"
import { useTableSummary } from "./table.summary.context"
import { useColumnSummary } from "./table.summary.values"
import TableSummaryBreakdown from "./TableSummaryBreakdown"
import TableSummaryLegend from "./TableSummaryLegend"
import TableSummaryMenu from "./TableSummaryMenu"
import TableSummaryVisual from "./TableSummaryVisual"

export type TableSummaryCellProps<TData = unknown> = {
  column: Column<TData, unknown>
  style?: CSSProperties
}

/**
 * One column's summary: the calculation it runs, the answer, and — for a
 * breakdown, a count, a share, an average or a median — a bar that pictures
 * it, all in a single button that opens the menu to change it. With none
 * set, it invites one. Long answers (a list of values) truncate and reveal
 * in full on hover, the way body cells do; a breakdown reveals its whole
 * legend.
 */
function TableSummaryCell<TData = unknown>(
  props: TableSummaryCellProps<TData>,
) {
  const { column, style } = props
  const { aggregations, setAggregation } = useTableSummary()
  const type = aggregations[column.id]
  const result = useColumnSummary(column, type)
  const breakdown = result?.visual?.type === "stack" ? result.visual : null
  const overflow = useOverflowPopover<HTMLSpanElement>({
    isAlwaysShown: breakdown !== null,
  })
  const isFigure =
    type !== undefined && !SUMMARY_LIST_AGGREGATIONS.includes(type)

  return (
    <td
      className="ui-table-summary-cell"
      style={style}
      onMouseEnter={overflow.onMouseEnter}
      onMouseLeave={overflow.onMouseLeave}
    >
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={cn("ui-table-summary-trigger", {
              "ui-table-summary-trigger-has-result": result !== null,
            })}
            aria-label={
              result ? undefined : `Calculate ${getColumnTitle(column)}`
            }
          >
            {result ? (
              <>
                <span className="ui-table-summary-label">{result.label}</span>
                <span
                  ref={overflow.ref}
                  className={cn("ui-table-summary-value", {
                    "ui-table-summary-value-is-figure": isFigure,
                  })}
                >
                  {breakdown ? (
                    <TableSummaryBreakdown entries={breakdown.entries} />
                  ) : (
                    result.value
                  )}
                </span>
                {result.visual && <TableSummaryVisual visual={result.visual} />}
              </>
            ) : (
              <span className="ui-table-summary-placeholder">
                Calculate
                <Icon src="angle-small-down" size={12} />
              </span>
            )}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" className="ui-table-summary-menu">
          <TableSummaryMenu
            column={column}
            value={type ?? null}
            onChange={(next) => {
              setAggregation(column.id, next)
            }}
          />
        </DropdownMenu.Content>
      </DropdownMenu>
      {result && overflow.popoverStyle && (
        <div className="ui-table-cell-popover" style={overflow.popoverStyle}>
          {breakdown ? (
            <TableSummaryLegend entries={breakdown.entries} />
          ) : (
            result.value
          )}
        </div>
      )}
    </td>
  )
}

export default TableSummaryCell
